import { Dropdown, Flex, Input, ProviderConsumer } from "@fluentui/react-northstar";
import _, { cloneDeep, isEqual } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { appState } from "../../../AppState";
import { CategoryList } from "../../../category/CategoryList";
import { CustomPropertyListItems } from "../../../custom-properties/CustomPropertyListItems";
import { CustomPropertyDataType_LABELS } from "../../../custom-properties/utils/constants";
import { PriorityList } from "../../../priority/PriorityList";
import { getCachedFeature } from "../../../shared/cache/FeatureCache";
import { Breadcrumbs } from "../../../shared/components/BreadcrumbNavigation";
import { LoadingScreen } from "../../../shared/components/LoadingScreen";
import { LabelElement } from "../../../shared/components/Modal/Modal";
import { Toolbar } from '../../../shared/components/Toolbar';
import { platformService } from "../../../shared/services/platform.service";
import { LICENSE_FEATURE, LOADING, TEMPLATE_DROPDOWN_RETURN_LABEL, TEMPLATE_DROPDOWN_RETURN_VALUE } from "../../../shared/utils/constants";
import { toTitleCase } from "../../../shared/utils/helper";
import { StatusList } from "../../../status/StatusList";
import { TicketTypeList } from "../../../ticket-type/TicketTypeList";
import { DataSourceOptionsList } from "./DataSourceOptionsList";
import { IDataSource } from "./DataSourceTable";

export interface TemplatePickerBaseProps {
  forDataSource: boolean | true;
  onAddRemoveFromDataSource?: (item: any, checked: boolean, idField?: string) => void;
  dataSourceData: IDataSource
}

interface Props{
  id: number,
  onBackToTable: () => void;
}

export const CustomElementDataSourceForm = ({id, onBackToTable}: Props) => {

    const { t } = useTranslation();
    const api = new platformService();
    const currentState = appState();
    const [data, setData] = useState<IDataSource>({Id: 0, Name: "", SelectedValues: []} as IDataSource);
    const [typeList, setTypeList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dataClone, setDataClone] = useState<any>(cloneDeep(data));

    const fetchData = useCallback(async () => {
        try {
          const result = await api.getDataSource(id);

          if(result.status == 200 && result.data){
            const valueData: IDataSource = result.data;
            valueData.SelectedValues = valueData.SelectedItems.split(",");
            valueData.SelectedItems = "";
            setData(valueData);
            setDataClone(cloneDeep(valueData));
          }
        } catch (err: any) {
        }
        setIsLoading(false);
      }, []);

    const fetchTypeList = useCallback(async () => {
      const results = await Promise.all([
          getCachedFeature(LICENSE_FEATURE.TicketTags),
          getCachedFeature(LICENSE_FEATURE.MultiDepartment),
          getCachedFeature(LICENSE_FEATURE.CustomForms),
          getCachedFeature(LICENSE_FEATURE.CustomProperties)
      ]);

      const isTagsEnabled = results[0];
      const isTeamsEnabled = results[1];
      const isCustomFormsEnabled = results[2];
      const isCustomPropertiesEnabled = results[3];

      let typesItems = [];
      for (const [key, value] of Object.entries(TEMPLATE_DROPDOWN_RETURN_LABEL)) {
        if(!isTagsEnabled && key == TEMPLATE_DROPDOWN_RETURN_VALUE.TAG) continue;
        if(!isTeamsEnabled && key == TEMPLATE_DROPDOWN_RETURN_VALUE.TEAM) continue;
        if(!isCustomFormsEnabled && key !== TEMPLATE_DROPDOWN_RETURN_VALUE.TEAM && key !== TEMPLATE_DROPDOWN_RETURN_VALUE.CATEGORY) continue;
        
        typesItems.push({
          key: key,
          label: value,
          header: value
        });
      }

      if(isCustomPropertiesEnabled){
        currentState?.customProperties
        .filter(c => !c.IsHidden && CustomPropertyDataType_LABELS[c.DataType] == CustomPropertyDataType_LABELS.LIST)
        .forEach(p => {
          typesItems.push({
            id: p.Id,
            key: "CustomProperty",
            label: p.Name,
            header: p.Name
          });
        });
      }

      setTypeList(typesItems);
    }, []);

    useEffect(() => {

      if(typeList.length == 0){
        fetchTypeList();
      }else if(id > 0)
        fetchData();
      else
        setIsLoading(false);
      
    }, [id, typeList]);

    const saveData = async() => {
      const payload: IDataSource = data;
      payload.SelectedItems = payload.SelectedValues.join();
      delete payload.SelectedValues;
  
      try {
        if(id == 0)
          await api.createDataSource(payload)
        else
          await api.updateDataSource(payload, id);

        onBackToTable && onBackToTable();
  
      } catch (err: any) {
        
      }
    };

    const navs: Breadcrumbs = {
      breadcrumbs: [
        {
          title: t('template.datasource.title-plural'),
          link: '',
          notALink: true,
          onClickHandler: onBackToTable
        },
        {
          title: (data?.Name ?? "").length > 0 ? data.Name : `${t('common.new')} ${ t('template.datasource.title')}`,
          link: ''
        }
      ]
    };

    const onAddRemoveFromDataSource = (item: any, checked: boolean, idField: string = "id") => {
      const idFieldStr = item[idField].toString();
      let newValues = data.SelectedValues;
      if(checked){
        newValues.push(idFieldStr);
      }else 
        newValues = newValues.filter(x => x != idFieldStr);
      setData({...data, SelectedValues: _.compact(_.uniq(newValues))});
    }

    const dataSourceProps:TemplatePickerBaseProps = {forDataSource: true, onAddRemoveFromDataSource: onAddRemoveFromDataSource, dataSourceData: data};

    return <ProviderConsumer
        render={globalTheme => (
          <>
          <Toolbar
            globalTheme={globalTheme}
            title={t('template.datasource.title-plural')}
            breadcrumbs={navs}
            saveText={t('common.buttons.save')}
            saveCommand={saveData}
            disableSave={(data.Name?.removeWhiteSpaces().length ?? 0) == 0 || (data.EntityType?.length ?? 0) == 0 || isEqual(data, dataClone)}
          />

          {/* Main Section */}
          {(isLoading && typeList.length == 0) ? <LoadingScreen message={`${LOADING.replace("...", "")} ${t('template.datasource.title-plural')}...`} /> :
          <Flex style={{ marginTop: '-1.25rem' }} className="height--150" column gap='gap.medium'>
            <Flex.Item grow styles={{ paddingTop: '1.25rem'}}>
              <div style={{ padding: 10 }}>
                <Flex column gap="gap.medium"
                  className={`w-full pt-4 h-full`}
                  styles={{
                      overflow: "auto",
                      paddingLeft: "15px",
                  }}>
                  <LabelElement label={toTitleCase(t('template.datasource-headers.name'))} required={true} color={ globalTheme.siteVariables.colorScheme.default.foreground }></LabelElement>
                  <div className="teams-input-med">
                    <Input
                        value={data.Name}
                        onChange={(_e, p) => {
                          setData({...data, Name: p.value?.toString()});
                        }}
                        autoFocus
                        fluid />
                  </div>
                  <LabelElement label={toTitleCase(t('template.datasource-headers.type'))} required={true} color={ globalTheme.siteVariables.colorScheme.default.foreground }></LabelElement>
                  <div className="teams-input-med">
                    <Dropdown fluid style={{background: globalTheme.siteVariables.colorScheme.default.background2 }}
                      items={typeList}
                      value={typeList.find(t => data.CustomPropertyId ? t.id == data.CustomPropertyId : t.key == data.EntityType)}
                      onChange={(_e, p) => {
                        setData({...data, EntityType: p.value["key"]?.toString(), CustomPropertyId: p.value["id"] ?? null});
                      }}
                    />
                  </div>
                  <div>
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.STATUS && <StatusList dataSourceProps={dataSourceProps} />}
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.TICKET_TYPE && <TicketTypeList dataSourceProps={dataSourceProps} />}
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.CATEGORY && <CategoryList dataSourceProps={dataSourceProps} />}
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.PRIORITY && <PriorityList dataSourceProps={dataSourceProps} />}
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.TAG && <DataSourceOptionsList dataSourceProps={dataSourceProps}/>}
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.TEAM && <DataSourceOptionsList dataSourceProps={dataSourceProps}/>}
                    {data.EntityType == TEMPLATE_DROPDOWN_RETURN_VALUE.GROUP && <DataSourceOptionsList dataSourceProps={dataSourceProps}/>}
                    {data.EntityType == "CustomProperty" && (
                      <div key={`CustomProperty_${data.CustomPropertyId}`}>
                        <CustomPropertyListItems propertyId={data.CustomPropertyId} dataSourceProps={dataSourceProps}/>
                      </div>
                    )}
                  </div>
                </Flex>
              </div>
            </Flex.Item>
          </Flex>
          }
        </>)}
    />
}